import { MOCK_CURRENT_USER } from '../../constants';
import { getEffectiveUsage } from '../../helpers/getEffectiveUsage';
import { DRONES, SEEDED_SERVICE_RECORDS } from '../../mockData';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type {
    Drone,
    LogService,
    PartType,
    ServiceRecord,
    ServiceRecordChange,
    ServiceRecordDraft,
    ServiceRecordEdit,
    ServiceRecordMap,
    UpdateService,
    UseServiceRecordsReturn,
    VoidService
} from './types';

// Raw (pre-reset) readings for a part's per-unit counters; aggregate parts have none.
// Every prop shares the airframe aggregate counter as its raw reading (FR-30b), so a
// per-prop snapshot still derives from the shared counter with its own unit number.
const getRawUnitUsages = ({ drone, partType }: { drone: Drone; partType: PartType }): number[] => {
    const unitReaders: Partial<Record<PartType, (target: Drone) => number[]>> = {
        flowMeter: target => [target.flowMeter.usage],
        motor: target => target.motors.map(motor => motor.usage),
        propSet: target => target.motors.map(() => target.usage.propSet ?? 0),
        pumpSealKit: target => target.pumps.map(pump => pump.usage)
    };

    return unitReaders[partType]?.(drone) ?? [];
};

// The usage snapshot stamped onto a new record: the current effective reading of the
// counter the record targets, via the same derivation applyServiceRecords uses.
// Calibrated and inspected records don't touch a counter, so they stamp 0.
const getUsageAtRecord = ({
    draft,
    drone,
    records
}: {
    draft: ServiceRecordDraft;
    drone: Drone;
    records: ServiceRecord[];
}): number => {
    if (draft.eventType === 'calibrated' || draft.eventType === 'inspected') {
        return 0;
    }

    const { partType, unitNumber } = draft;
    const rawUnitUsages = getRawUnitUsages({ drone, partType });

    if (rawUnitUsages.length === 0) {
        return getEffectiveUsage({
            partType,
            rawUsage: drone.usage[partType] ?? 0,
            records
        });
    }

    if (unitNumber !== undefined) {
        const rawUsage = rawUnitUsages[unitNumber - 1];

        if (rawUsage === undefined) {
            return 0;
        }

        return getEffectiveUsage({ partType, rawUsage, records, unitNumber });
    }

    // No unit number targets every unit; snapshot the highest effective reading so the
    // clamp in the reset derivation zeroes each unit.
    return Math.max(
        0,
        ...rawUnitUsages.map((rawUsage, index) =>
            getEffectiveUsage({ partType, rawUsage, records, unitNumber: index + 1 })
        )
    );
};

// Mirrors buildDraft's trim-to-undefined: a cleared field and a never-set field are
// the same non-value, so neither direction of that pair counts as a change.
const normalizeText = (value: string | undefined): string => value?.trim() ?? '';

// Diffs an edit against the stored record and applies it, appending one audit entry
// per changed field. Returns null when nothing changed so no-op saves create no
// audit entries.
const getUpdatedRecord = ({
    edit,
    record
}: {
    edit: ServiceRecordEdit;
    record: ServiceRecord;
}): ServiceRecord | null => {
    const changedAt = new Date().toISOString();
    const changes: ServiceRecordChange[] = [];

    const stampChange = ({
        fieldChanged,
        newValue,
        oldValue
    }: Pick<ServiceRecordChange, 'fieldChanged' | 'newValue' | 'oldValue'>) => {
        changes.push({ changedAt, changedBy: MOCK_CURRENT_USER, fieldChanged, newValue, oldValue });
    };

    const previousNotes = normalizeText(record.notes);
    const nextNotes = normalizeText(edit.notes);
    const areNotesChanged = nextNotes !== previousNotes;

    if (areNotesChanged) {
        stampChange({ fieldChanged: 'notes', newValue: nextNotes, oldValue: previousNotes });
    }

    // A serial only exists on part-targeted records; calibrations never carry one.
    const partRecord = record.eventType === 'calibrated' ? undefined : record;
    const previousSerialNumber = normalizeText(partRecord?.partSerialNumber);
    const nextSerialNumber = partRecord ? normalizeText(edit.partSerialNumber) : '';
    const isSerialNumberChanged = nextSerialNumber !== previousSerialNumber;

    if (isSerialNumberChanged) {
        stampChange({
            fieldChanged: 'partSerialNumber',
            newValue: nextSerialNumber,
            oldValue: previousSerialNumber
        });
    }

    // Compare by day: the picker commits day granularity (midnight), while the stored
    // performedAt carries the log-time clock time — a timestamp compare would flag
    // every re-save of an untouched date as a change.
    const isPerformedAtChanged =
        new Date(edit.performedAt).toDateString() !== new Date(record.performedAt).toDateString();

    if (isPerformedAtChanged) {
        stampChange({
            fieldChanged: 'performedAt',
            newValue: edit.performedAt,
            oldValue: record.performedAt
        });
    }

    // Photos diff by id: an id present on only one side is one add/remove entry, so a
    // pure reorder is dropped (latent — no reorder affordance exists today). Removed
    // photos' object URLs are NEVER revoked here: a removal can be cancelled, and a
    // currently-rendered detail view may still hold the old array.
    const previousPhotos = record.photos ?? [];
    const nextPhotos = edit.photos;
    const previousPhotoIds = new Set(previousPhotos.map(photo => photo.id));
    const nextPhotoIds = new Set(nextPhotos.map(photo => photo.id));

    previousPhotos
        .filter(photo => !nextPhotoIds.has(photo.id))
        .forEach(photo =>
            stampChange({ fieldChanged: 'photoRemoved', newValue: '', oldValue: photo.fileName })
        );

    nextPhotos
        .filter(photo => !previousPhotoIds.has(photo.id))
        .forEach(photo =>
            stampChange({ fieldChanged: 'photoAdded', newValue: photo.fileName, oldValue: '' })
        );

    if (changes.length === 0) {
        return null;
    }

    // Applying a performedAt edit re-orders wear/recency derivations by design;
    // usageAtRecord stays log-time truth (known prototype limitation, deliberate).
    // Fields write back only when their change was stamped: an un-audited
    // normalization (a trim, or the picker's midnight replacing the stored clock
    // time on an unchanged day) must never silently rewrite the record.
    const editedBase = {
        changeLog: [...(record.changeLog ?? []), ...changes],
        notes: areNotesChanged ? nextNotes || undefined : record.notes,
        performedAt: isPerformedAtChanged ? edit.performedAt : record.performedAt,
        photos: nextPhotos.length > 0 ? nextPhotos : undefined
    };

    return record.eventType === 'calibrated'
        ? { ...record, ...editedBase }
        : {
              ...record,
              ...editedBase,
              partSerialNumber: isSerialNumberChanged
                  ? nextSerialNumber || undefined
                  : record.partSerialNumber
          };
};

// Owns the per-drone service log streams. logService stamps the audit fields the form
// never supplies (id, by, at, usageAtRecord); updateService applies field edits and
// stamps the resulting changeLog entries; voidService soft-deletes a record so the
// derivations in applyServiceRecords ignore it while the audit trail stays intact.
const useServiceRecords = (): UseServiceRecordsReturn => {
    const [serviceRecords, setServiceRecords] = useState<ServiceRecordMap>(SEEDED_SERVICE_RECORDS);

    const logService: LogService = ({ draft, droneId }) => {
        const drone = DRONES.find(candidate => candidate.id === droneId);

        if (!drone) {
            return;
        }

        const record: ServiceRecord = {
            ...draft,
            at: new Date().toISOString(),
            by: MOCK_CURRENT_USER,
            id: uuidv4(),
            usageAtRecord: getUsageAtRecord({
                draft,
                drone,
                records: serviceRecords[droneId] ?? []
            })
        };

        setServiceRecords(current => ({
            ...current,
            [droneId]: [...(current[droneId] ?? []), record]
        }));
    };

    // The diff runs inside the functional updater — unlike logService's outside read,
    // this can never compare against a stale record snapshot.
    const updateService: UpdateService = ({ droneId, edit, recordId }) => {
        setServiceRecords(current => {
            const records = current[droneId] ?? [];
            const record = records.find(candidate => candidate.id === recordId);

            if (!record || record.voided) {
                return current;
            }

            const updatedRecord = getUpdatedRecord({ edit, record });

            if (!updatedRecord) {
                return current;
            }

            return {
                ...current,
                [droneId]: records.map(candidate =>
                    candidate.id === recordId ? updatedRecord : candidate
                )
            };
        });
    };

    const voidService: VoidService = ({ droneId, recordId }) => {
        const voidedAt = new Date().toISOString();

        setServiceRecords(current => ({
            ...current,
            [droneId]: (current[droneId] ?? []).map(record =>
                record.id === recordId
                    ? { ...record, voided: true, voidedAt, voidedBy: MOCK_CURRENT_USER }
                    : record
            )
        }));
    };

    return { logService, serviceRecords, updateService, voidService };
};

export default useServiceRecords;
